import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { HardDrive, Users, Activity, AlertTriangle, Download, Share2, Music, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const API = "https://magicmixapp.ch/api";

const AdminDashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // --- MOTORE DI RICERCA E PAGINAZIONE ---
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 50;

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/stats`, { withCredentials: true });
        setStats(response.data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.detail || "Accesso negato. Autorizzazione non valida.");
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchStats();
    } else {
      setError("Devi effettuare l'accesso con l'account amministratore.");
      setLoading(false);
    }
  }, [user]);

  if (loading) {
    return <div style={{ color: '#00f2fe', padding: '50px', textAlign: 'center', backgroundColor: '#09090b', minHeight: '100vh', fontFamily: 'monospace' }}>CARICAMENTO DATI DI SISTEMA...</div>;
  }

  if (error) {
    return (
      <div style={{ color: '#ef4444', padding: '50px', textAlign: 'center', backgroundColor: '#09090b', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <AlertTriangle size={64} style={{ marginBottom: '20px' }} />
        <h2 style={{ letterSpacing: '2px' }}>ACCESSO NEGATO</h2>
        <p style={{ color: '#888' }}>{error}</p>
      </div>
    );
  }

  const diskColor = stats.disk.percent_used > 80 ? '#ef4444' : stats.disk.percent_used > 60 ? '#eab308' : '#10b981';

  // --- LOGICA FILTRO E PAGINAZIONE UTENTI ---
  const filteredUsers = stats.users.filter(u => 
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div style={{ backgroundColor: '#09090b', minHeight: '100vh', color: '#fff', padding: '20px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <header style={{ borderBottom: '1px solid #27272a', paddingBottom: '20px', marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ color: '#00f0ff', margin: 0, fontSize: '24px', letterSpacing: '2px', textTransform: 'uppercase' }}>Magic Mix | Cabina di Regia</h1>
        <div style={{ fontSize: '12px', color: '#888' }}>Admin: {user.email}</div>
      </header>

      {/* BLOCCO 1: SALA MACCHINE (DISCO) */}
      <section style={{ backgroundColor: '#18181b', padding: '20px', borderRadius: '12px', border: '1px solid #27272a', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
          <HardDrive color={diskColor} />
          <h2 style={{ margin: 0, fontSize: '18px' }}>Stato Server (Spazio Disco)</h2>
        </div>
        <div style={{ backgroundColor: '#27272a', borderRadius: '8px', height: '24px', width: '100%', overflow: 'hidden', position: 'relative' }}>
          <div style={{ backgroundColor: diskColor, width: `${stats.disk.percent_used}%`, height: '100%', transition: 'width 0.5s ease-in-out' }}></div>
          <span style={{ position: 'absolute', top: '2px', left: '10px', fontSize: '12px', fontWeight: 'bold', textShadow: '1px 1px 2px #000' }}>
            {stats.disk.percent_used}% Utilizzato ({stats.disk.used_gb} GB su {stats.disk.total_gb} GB)
          </span>
        </div>
      </section>

      {/* BLOCCO 2: CONTATORI BUSINESS */}
      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px' }}>
        <div style={{ backgroundColor: '#18181b', padding: '20px', borderRadius: '12px', border: '1px solid #27272a', textAlign: 'center' }}>
          <Users size={32} color="#3b82f6" style={{ margin: '0 auto 10px' }} />
          <div style={{ fontSize: '32px', fontWeight: '900' }}>{stats.users_count}</div>
          <div style={{ color: '#888', fontSize: '12px', textTransform: 'uppercase' }}>Utenti Totali</div>
        </div>
        
        <div style={{ backgroundColor: '#18181b', padding: '20px', borderRadius: '12px', border: '1px solid #27272a', textAlign: 'center' }}>
          <Activity size={32} color="#10b981" style={{ margin: '0 auto 10px' }} />
          <div style={{ fontSize: '32px', fontWeight: '900' }}>{stats.exports.total}</div>
          <div style={{ color: '#888', fontSize: '12px', textTransform: 'uppercase' }}>Mix Esportati</div>
        </div>

        <div style={{ backgroundColor: '#18181b', padding: '20px', borderRadius: '12px', border: '1px solid #27272a', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
            <div>
              <Share2 size={24} color="#ec4899" style={{ margin: '0 auto 5px' }} />
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{stats.exports.social}</div>
              <div style={{ color: '#888', fontSize: '10px' }}>SOCIAL VIDEO</div>
            </div>
            <div>
              <Music size={24} color="#eab308" style={{ margin: '0 auto 5px' }} />
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{stats.exports.mp3}</div>
              <div style={{ color: '#888', fontSize: '10px' }}>AUDIO MP3</div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCCO 3: ANAGRAFICA UTENTI */}
      <section style={{ backgroundColor: '#18181b', padding: '20px', borderRadius: '12px', border: '1px solid #27272a', overflowX: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Download size={20} /> Anagrafica Clienti
          </h2>
          
          {/* BARRA DI RICERCA */}
          <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#09090b', border: '1px solid #3b82f6', borderRadius: '8px', padding: '5px 10px', width: '100%', maxWidth: '300px' }}>
            <Search size={16} color="#3b82f6" />
            <input 
              type="text" 
              placeholder="Cerca per nome o email..." 
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Torna alla pagina 1 quando cerchi
              }}
              style={{ backgroundColor: 'transparent', border: 'none', color: '#fff', outline: 'none', width: '100%', marginLeft: '10px', fontSize: '14px' }}
            />
          </div>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #27272a', color: '#888' }}>
              <th style={{ padding: '10px' }}>Data</th>
              <th style={{ padding: '10px' }}>Nome</th>
              <th style={{ padding: '10px' }}>Email</th>
              <th style={{ padding: '10px' }}>Indirizzo IP</th>
              <th style={{ padding: '10px' }}>Stato</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.length > 0 ? (
              currentUsers.map((u, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #27272a' }}>
                  <td style={{ padding: '10px', color: '#bbb' }}>{new Date(u.created_at).toLocaleDateString()}</td>
                  <td style={{ padding: '10px', fontWeight: 'bold' }}>{u.name}</td>
                  <td style={{ padding: '10px' }}>{u.email}</td>
                  <td style={{ padding: '10px', fontFamily: 'monospace', color: '#00f2fe' }}>{u.last_ip || 'Sconosciuto'}</td>
                  <td style={{ padding: '10px' }}>
                    <span style={{ backgroundColor: u.status === 'Pro' || u.status === 'Premium' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.1)', color: u.status === 'Pro' || u.status === 'Premium' ? '#10b981' : '#ccc', padding: '4px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>
                      {u.status || 'Free'}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#888' }}>Nessun utente trovato.</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* PAGINAZIONE */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #27272a' }}>
            <button 
              onClick={() => paginate(currentPage - 1)} 
              disabled={currentPage === 1}
              style={{ display: 'flex', alignItems: 'center', gap: '5px', backgroundColor: currentPage === 1 ? '#09090b' : '#27272a', color: currentPage === 1 ? '#555' : '#fff', border: 'none', padding: '8px 15px', borderRadius: '8px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
            >
              <ChevronLeft size={16} /> Precedente
            </button>
            
            <div style={{ fontSize: '12px', color: '#888' }}>
              Pagina <span style={{ color: '#fff', fontWeight: 'bold' }}>{currentPage}</span> di {totalPages}
            </div>

            <button 
              onClick={() => paginate(currentPage + 1)} 
              disabled={currentPage === totalPages}
              style={{ display: 'flex', alignItems: 'center', gap: '5px', backgroundColor: currentPage === totalPages ? '#09090b' : '#27272a', color: currentPage === totalPages ? '#555' : '#fff', border: 'none', padding: '8px 15px', borderRadius: '8px', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
            >
              Successiva <ChevronRight size={16} />
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminDashboard;